import { TableCell, TableRow, IconButton, Tooltip, Collapse, Box, CircularProgress } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { ExecucaoCiclo, HistoricoExecucaoCicloResponse, TipoStatusExecucaoCiclo } from '../../../../types/execucaoCicloTypes'
import automacaoManager from '../../../../services/api/automacao/automacaoManager'
import { customTheme } from '../../../../styles/customTheme'

interface LinhaTabelaValidacaoExecucaoCicloProps {
  item: ExecucaoCiclo
  onAceitar: (execucao: ExecucaoCiclo) => void
  onRejeitar: (execucao: ExecucaoCiclo) => void
}

export function LinhaTabelaValidacaoExecucaoCiclo({ item, onAceitar, onRejeitar }: LinhaTabelaValidacaoExecucaoCicloProps) {
  const [aberto, setAberto] = useState(false)
  const [carregandoHistorico, setCarregandoHistorico] = useState(false)
  const [historico, setHistorico] = useState<HistoricoExecucaoCicloResponse[] | null>(null)

  const toggle = () => {
    const novo = !aberto
    setAberto(novo)
    if (novo && historico === null) {
      setCarregandoHistorico(true)
      automacaoManager
        .listarHistoricoExecucaoCicloApi(item.cicloId)
        .then((dados) => {
          const ordenar = (lista: HistoricoExecucaoCicloResponse[]) =>
            [...lista].sort((a, b) => new Date(b.horarioAlteracao as any).getTime() - new Date(a.horarioAlteracao as any).getTime())
          setHistorico(ordenar(dados))
        })
        .catch((err) => {
          const message = err?.message || 'Erro ao buscar histórico'
          toast.error(message)
        })
        .finally(() => setCarregandoHistorico(false))
    }
  }
  const getDataInicio = () => {
    return item.dataInicialAjustada || item.dataInicialOriginal
  }

  const getDataFim = () => {
    return item.dataFinalAjustada || item.dataFinalOriginal
  }

  const doisDigitos = (n: number) => (n < 10 ? `0${n}` : `${n}`)

  const formatarData = (data?: Date | string | null) => {
    if (!data) return ''

    if (typeof data === 'string') {
      const iso = data
      const [dataParte, tempoParte] = iso.split('T')
      if (dataParte && tempoParte) {
        const [ano, mes, dia] = dataParte.split('-')
        const [hora, minuto] = tempoParte.split(':')
        return `${doisDigitos(Number(dia))}/${doisDigitos(Number(mes))}/${ano} ${doisDigitos(Number(hora))}:${doisDigitos(Number(minuto))}`
      }
    }

    const d = data instanceof Date ? data : new Date(data as any)
    if (isNaN(d.getTime())) return ''
    const dia = doisDigitos(d.getDate())
    const mes = doisDigitos(d.getMonth() + 1)
    const ano = d.getFullYear()
    const hora = doisDigitos(d.getHours())
    const minuto = doisDigitos(d.getMinutes())
    return `${dia}/${mes}/${ano} ${hora}:${minuto}`
  }

  const formatarDataLocal = (data?: Date | string | null) => {
    if (!data) return ''
    const d = new Date(data as any)
    if (isNaN(d.getTime())) return ''
    const ajustado = new Date(d.getTime() - 3 * 60 * 60 * 1000)
    const dia = doisDigitos(ajustado.getDate())
    const mes = doisDigitos(ajustado.getMonth() + 1)
    const ano = ajustado.getFullYear()
    const hora = doisDigitos(ajustado.getHours())
    const minuto = doisDigitos(ajustado.getMinutes())
    return `${dia}/${mes}/${ano} ${hora}:${minuto}`
  }

  const getTipoEnvioText = () => (item.tipoEnvio === 1 ? 'Planilha' : 'API')

  const getStatusTextoByNumber = (status: number) => {
    const mapa: Record<number, string> = {
      [TipoStatusExecucaoCiclo.Novo]: 'Novo',
      [TipoStatusExecucaoCiclo.ErroAquivo]: 'ErroArquivo',
      [TipoStatusExecucaoCiclo.ErroValidacao]: 'ErroValidação',
      [TipoStatusExecucaoCiclo.Validado]: 'Validado',
      [TipoStatusExecucaoCiclo.Divergente]: 'Divergente',
      [TipoStatusExecucaoCiclo.Rejeitado]: 'Rejeitado',
      [TipoStatusExecucaoCiclo.Aprovado]: 'Aprovado',
      [TipoStatusExecucaoCiclo.Sucesso]: 'Sucesso'
    }
    return mapa[status] ?? `${status}`
  }
  const getStatusTexto = () => getStatusTextoByNumber(item.status)

  const getStatusColor = () => {
    switch (item.status) {
      case TipoStatusExecucaoCiclo.Divergente:
        return customTheme.colors.orange01
      case TipoStatusExecucaoCiclo.Aprovado:
        return customTheme.colors.azul05
      case TipoStatusExecucaoCiclo.Rejeitado:
        return customTheme.colors.red05
      case TipoStatusExecucaoCiclo.Novo:
        return customTheme.colors.yellow01
      case TipoStatusExecucaoCiclo.Validado:
        return customTheme.colors.leroy
      case TipoStatusExecucaoCiclo.Sucesso:
        return customTheme.colors.green05
      default:
        return customTheme.colors.cinza08
    }
  }

  const renderAcoes = () => {
    if (item.status !== TipoStatusExecucaoCiclo.Divergente) return null
    return (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <Tooltip title="Aceitar">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onAceitar(item)
            }}
            style={{ color: customTheme.colors.green06 }}
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rejeitar">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onRejeitar(item)
            }}
            style={{ color: customTheme.colors.red06 }}
          >
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </div>
    )
  }

  return (
    <>
      <TableRow hover onClick={toggle} style={{ cursor: 'pointer' }} aria-expanded={aberto}>
        <TableCell align="center">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <KeyboardArrowDownIcon
              style={{ transition: 'transform 150ms ease', transform: aberto ? 'rotate(180deg)' : 'rotate(0deg)', color: customTheme.colors.cinza07 }}
            />
            {item.cicloId}
          </span>
        </TableCell>
        <TableCell align="center">{formatarData(getDataInicio())}</TableCell>
        <TableCell align="center">{formatarData(getDataFim())}</TableCell>
        <TableCell align="center">{getTipoEnvioText()}</TableCell>
        <TableCell align="center">
          <span style={{ color: getStatusColor(), fontWeight: 'bold' }}>{getStatusTexto()}</span>
        </TableCell>
        <TableCell align="center">{renderAcoes()}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={aberto} timeout="auto" unmountOnExit>
            <Box padding={2}>
              {carregandoHistorico && (
                <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
                  <CircularProgress size={20} style={{ marginRight: 8 }} /> Carregando histórico...
                </Box>
              )}
              {!carregandoHistorico && historico && historico.length === 0 && <div>Não possui histórico de execução para este ciclo.</div>}
              {!carregandoHistorico && historico && historico.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                  {historico.map((h) => (
                    <div key={h.id} style={{ padding: '4px 0' }}>
                      <div>
                        <strong>Horário:</strong> {formatarDataLocal(h.horarioAlteracao as any)}
                      </div>
                      <div>
                        <strong>Status:</strong> {getStatusTextoByNumber(h.status)}
                      </div>
                      <div>
                        <strong>Funcionário:</strong> {h.funcionario}
                      </div>
                      <div>
                        <strong>Ciclo:</strong> {h.cicloId}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}
